import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core'
import EventChip from './EventChip'
import { isPublished } from './utils'

const useStyles = makeStyles((theme) => ({
  cell: {
    minHeight: 104,
    height: '100%',
    minWidth: 0,
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    backgroundColor: '#fff',
    border: '1px solid #eaeef6',
    borderRadius: 8,
  },
  outside: {
    backgroundColor: '#f6f8fc',
    border: 'none',
  },
  number: {
    fontSize: 13,
    fontWeight: 500,
    color: '#686868',
    lineHeight: '22px',
  },
  today: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 600,
  },
  mobileCell: {
    minHeight: 44,
    padding: 4,
    borderRadius: 6,
    gap: 2,
    alignItems: 'center',
  },
  tappable: {
    width: '100%',
    font: 'inherit',
    cursor: 'pointer',
  },
  selected: { border: `2px solid ${theme.palette.primary.main}`, padding: 3 },
  dots: { display: 'flex', gap: 2, justifyContent: 'center' },
  dotSmall: { width: 5, height: 5, borderRadius: '50%' },
  published: { backgroundColor: '#008e5b' },
  draft: { backgroundColor: '#7f889c' },
}))

const MobileDayCell = ({ day, isToday, events, selected, onSelectDay }) => {
  const styles = useStyles()
  const content = (
    <>
      <span className={clsx(styles.number, isToday && styles.today)}>{day.date()}</span>
      {events.length > 0 && (
        <span className={styles.dots}>
          {events.slice(0, 3).map((event) => (
            <span
              key={event.id}
              data-testid="event-dot"
              className={clsx(styles.dotSmall, isPublished(event.status) ? styles.published : styles.draft)}
            />
          ))}
        </span>
      )}
    </>
  )

  if (events.length > 0 && onSelectDay) {
    return (
      <button
        type="button"
        className={clsx(styles.cell, styles.mobileCell, styles.tappable, selected && styles.selected)}
        onClick={() => onSelectDay(day)}
      >
        {content}
      </button>
    )
  }

  return <div className={clsx(styles.cell, styles.mobileCell)}>{content}</div>
}

MobileDayCell.propTypes = {
  day: PropTypes.object.isRequired,
  isToday: PropTypes.bool.isRequired,
  events: PropTypes.array.isRequired,
  selected: PropTypes.bool,
  onSelectDay: PropTypes.func,
}

MobileDayCell.defaultProps = {
  selected: false,
  onSelectDay: undefined,
}

const DayCell = ({ day, inMonth, isToday, events, onEventClick, mobile, selected, onSelectDay }) => {
  const styles = useStyles()

  if (!inMonth) {
    return <div className={clsx(styles.cell, mobile && styles.mobileCell, styles.outside)} />
  }

  if (mobile) {
    return <MobileDayCell day={day} isToday={isToday} events={events} selected={selected} onSelectDay={onSelectDay} />
  }

  return (
    <div className={styles.cell}>
      <span>
        <span className={clsx(styles.number, isToday && styles.today)}>{day.date()}</span>
      </span>
      {events.map((event) => (
        <EventChip key={event.id} event={event} onClick={onEventClick} />
      ))}
    </div>
  )
}

DayCell.propTypes = {
  day: PropTypes.object.isRequired,
  inMonth: PropTypes.bool.isRequired,
  isToday: PropTypes.bool.isRequired,
  events: PropTypes.array.isRequired,
  onEventClick: PropTypes.func,
  mobile: PropTypes.bool,
  selected: PropTypes.bool,
  onSelectDay: PropTypes.func,
}

DayCell.defaultProps = {
  onEventClick: undefined,
  mobile: false,
  selected: false,
  onSelectDay: undefined,
}

export default DayCell
